import { getChatScreenPalette } from '@/components/assistant-ui/chatScreenSpec';

export type DrawerVisualPalette = {
  background: string;
  textSecondary: string;
  headerSurfaceFallback: string;
  quickActionSurfaceFallback: string;
  quickActionSurfaceBorder: string;
  quickActionPressedBg: string;
  toolsSurfaceFallback: string;
  divider: string;
  listPressedBg: string;
  utilitySurface: string;
  popoutSurface: string;
  popoutBorder: string;
  ctaFill: string;
  ctaIcon: string;
  upgradeAccent: string;
};

// Geometry of the native drawer (mock: wordmark + search on top, flat rows,
// Chats, and a pinned Chat pill + settings gear at the bottom).
export const DRAWER_VISUAL_SPEC = {
  openWidthRatio: 0.73,
  maxWidth: 360,
  minVisiblePanel: 64,
  openPanelRadius: 56,
  openPanelWash: 0.62,
  overlay: 'transparent',
  topPaddingIOS: 58,
  topPaddingAndroid: 46,
  horizontalPadding: 12,
  headerGap: 12,
  /** Round header control (search) and its pressable footprint. */
  headerControlSize: 44,
  headerControlRadius: 22,
  /** Space between the header row and the first nav row (part of the pinned block). */
  headerBottomGap: 8,
  /** tabbio wordmark: asset is 228x54, drawn at 1/2.25 so it sits at control height. */
  wordmarkWidth: 101,
  wordmarkHeight: 24,
  searchInlineHeight: 46,
  /** Flat destination rows (My CV, Jobs, Tasks, ...). */
  navRowHeight: 48,
  navRowRadius: 14,
  navIconSize: 24,
  navLabelSize: 17,
  threadRowHeight: 42,
  threadRowRadius: 12,
  /** Pinned footer: the purple Chat pill and the settings circle. */
  footerPaddingTop: 12,
  footerPaddingBottom: 12,
  footerControlSize: 56,
  chatFabHeight: 56,
  chatFabRadius: 28,
  chatFabIconSize: 20,
  chatFabHorizontalPadding: 22,
  chatFabLabelSize: 17,
  workspaceBadgeSize: 22,
} as const;

/** Match the reference phone proportions while keeping the panel reachable. */
export function getDrawerWidth(windowWidth: number) {
  const width = Math.max(0, windowWidth);
  return Math.max(0, Math.min(
    DRAWER_VISUAL_SPEC.maxWidth,
    Math.round(width * DRAWER_VISUAL_SPEC.openWidthRatio),
    width - DRAWER_VISUAL_SPEC.minVisiblePanel,
  ));
}

// First-frame estimate of the drawer's pinned header block (wordmark row +
// its bottom gap), used until its onLayout reports the real height. The
// inline search row is not part of the estimate: it only exists after a tap,
// by which time onLayout has already corrected the measurement.
export function estimateDrawerHeaderBlockHeight(topPadding: number) {
  return (
    topPadding + DRAWER_VISUAL_SPEC.headerControlSize + DRAWER_VISUAL_SPEC.headerBottomGap
  );
}

// First-frame estimate of the pinned footer (Chat pill + gear), paid back as
// the scroll body's bottom padding until onLayout lands.
export function estimateDrawerFooterBlockHeight(bottomInset: number) {
  return (
    DRAWER_VISUAL_SPEC.footerPaddingTop +
    DRAWER_VISUAL_SPEC.footerControlSize +
    Math.max(bottomInset, DRAWER_VISUAL_SPEC.footerPaddingBottom)
  );
}

export function getDrawerVisualPalette(isDark: boolean): DrawerVisualPalette {
  const chatPalette = getChatScreenPalette(isDark);

  return {
    background: chatPalette.background,
    textSecondary: chatPalette.secondaryText,
    headerSurfaceFallback: chatPalette.headerFallbackBg,
    quickActionSurfaceFallback: chatPalette.headerFallbackBg,
    quickActionSurfaceBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    quickActionPressedBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    toolsSurfaceFallback: isDark ? '#141414' : '#ECECEF',
    divider: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    listPressedBg: isDark ? '#111111' : '#EDEDF0',
    utilitySurface: isDark ? '#131313' : '#ECECEF',
    popoutSurface: isDark ? '#141414' : '#FFFFFF',
    popoutBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    ctaFill: chatPalette.primaryRoundFill,
    ctaIcon: chatPalette.primaryRoundIcon,
    upgradeAccent: chatPalette.upgradeAccent,
  };
}
